import { defineStore } from 'pinia';
import api, { ApiError } from '@/api2/client';
import { reportsLegacyShape } from '@/api2/clinicalBridge';

/** The chart a load has been asked for and not yet started. */
let requested = null;

/**
 * Bumped by every load, invalidation and clear: only the newest read's answer
 * is kept, so a read that started before the chart's reports changed can never
 * land after the read that followed the change.
 */
let generation = 0;

/**
 * The open chart's saved reports, for the screens that read them out of
 * `getAllData` — the Reports page (`store/modules/report.js`, `allData.reports`)
 * and the PDF save modal's "recent choices" (`allData.reports[].procCcd`,
 * `allData.recent.report.ccd`, labelled through `allData.ccds`).
 *
 * pacmny-be shipped them inside `getAllData`, built null-safely after main's
 * fix so a report with only a category, or only a procedure, is still listed.
 * The port's `getAllData` is `/me/bootstrap`, which knows nothing about a chart,
 * so both screens were empty. `GET /reports?filter[patient_id]=` is the same
 * list; it is loaded once per chart, the first time something reads it, and
 * again after `invalidate()`, and the `login/getAllData` getter lays it over
 * the blob.
 *
 * A refusal (no `documents:read`) is remembered as "no reports" for that chart
 * rather than retried on every render.
 */
export const useChartReportsStore = defineStore('chartReports', {
  state: () => ({
    /** The chart the rows below belong to. */
    loadedFor: null,
    /** The chart a request is in flight for. */
    loadingFor: null,
    reports: [],
    /** The rows are the chart's, but something may have changed them since. */
    stale: false,
  }),

  getters: {
    legacyShape: (state) => reportsLegacyShape(state.reports),
  },

  actions: {
    async load(patientId) {
      const target = patientId === null || patientId === undefined ? null : String(patientId);

      if (target === null) {
        this.clear();

        return;
      }

      generation += 1;
      const ticket = generation;
      this.loadingFor = target;

      try {
        const page = await api.list('/reports', {
          'filter[patient_id]': target,
          include: 'category,procedureCodes,orderingProvider',
          sort: '-report_date',
          per_page: 100,
        });

        if (ticket === generation) {
          this.reports = page.items || [];
          this.loadedFor = target;
          this.stale = false;
        }
      } catch (error) {
        // A background read the page never asked for must not surface as an
        // unhandled rejection: whatever went wrong (a refusal, the network),
        // the chart shows no saved reports rather than breaking the screen.
        if (!(error instanceof ApiError) && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[chartReports] could not load the chart\'s reports', error);
        }

        if (ticket === generation) {
          this.reports = [];
          this.loadedFor = target;
          this.stale = false;
        }
      } finally {
        if (ticket === generation) this.loadingFor = null;
      }
    },

    /**
     * Load the chart's reports unless they are loaded (and not stale) or
     * loading. Called from a getter, so it defers the request to a microtask
     * rather than changing state while the getter is being evaluated.
     */
    ensureFor(patientId) {
      const target = patientId === null || patientId === undefined ? null : String(patientId);

      if (target === null || (target === this.loadedFor && !this.stale) || target === this.loadingFor || target === requested) return;

      // Outside the store's state on purpose: this runs while a getter is
      // being evaluated, and writing reactive state there re-triggers it.
      requested = target;
      Promise.resolve()
        .then(() => this.load(target))
        .finally(() => {
          if (requested === target) requested = null;
        });
    },

    /**
     * The chart's reports may have changed (`login/updateApplicationModel`,
     * which pacmny-be answered with the rebuilt list): re-read them the next
     * time they are read. The rows stay on screen until then - clearing them
     * blanked the Reports list and the PDF save modal's recent choices until
     * the re-read answered - and a read already under way is abandoned,
     * because it may have started before the change.
     */
    invalidate() {
      generation += 1;
      this.loadingFor = null;
      this.stale = true;
      requested = null;
    },

    clear() {
      generation += 1;
      this.loadedFor = null;
      this.loadingFor = null;
      this.reports = [];
      this.stale = false;
    },
  },
});

/**
 * The overlay the `login/getAllData` getter spreads: this chart's reports when
 * they are loaded, and a request for them when they are not.
 */
export function chartReportsForGetAllData(patientId, ccds = undefined) {
  const store = useChartReportsStore();

  if (patientId === null || patientId === undefined || patientId === '') return {};

  store.ensureFor(patientId);

  if (String(patientId) !== store.loadedFor) return {};

  const shape = store.legacyShape;

  // `ccds` is shared with the vitals screens, which label measurements through
  // the same map; the reports' codes are added to it, never swapped for it.
  return { ...shape, ccds: { ...(ccds || {}), ...shape.ccds } };
}
